import { useContext, useState, useEffect } from 'react';
import { FormGroup, Label, Button, FormText, ButtonGroup } from 'reactstrap';
import UploadDocument from './UploadDocuments';
import { DocumentInfoButton } from './DocumentInfoModal';
import { useFormContext } from 'react-hook-form';
import { TextInput, SelectInput } from '~/shared/components/form/Inputs';
import { FundRequestContext } from '~/context/fundrequests';
import { toUSD } from '~/shared/utils';

const FoodExpenseForm = ({ fieldArray, handleSubmission }) => {
    const {
        control,
        register,
        watch,
        setValue,
        formState: { errors, isSubmitting, isDirty, isValid },
    } = useFormContext();
    const [
        { expenseItem, expenseIndex, formKey, annualRates },
        fundRequestDispatch,
    ] = useContext(FundRequestContext);
    const { remove } = fieldArray;
    const hasValidId = typeof expenseItem?.id === 'number';
    const vals = watch(formKey);
    const existingUploads = watch(`${formKey}.existing_uploads`);
    const [totalDays, setTotalDays] = useState(0);
    const [currentPerDiemRate, setCurrentPerDiemRate] = useState(0);

    const handleDeleteItem = () => {
        remove(expenseIndex);
        fundRequestDispatch({
            type: 'reset_expense_item',
        });
    };

    const handleCancelItem = () => {
        if (!expenseItem.id) {
            handleDeleteItem();
        } else {
            fundRequestDispatch({
                type: 'reset_expense_item',
            });
        }
    };

    const getTotalDays = (startDate, endDate) => {
        const start = new Date(startDate);
        const end = new Date(endDate);

        if (isNaN(start.getTime()) || isNaN(end.getTime())) {
            throw new Error('Invalid date format');
        }

        const differenceInTime = end.getTime() - start.getTime();
        
        const days =
            differenceInTime === 0
                ? 1
                : differenceInTime > 0
                    ? Math.ceil(differenceInTime / (1000 * 3600 * 24)) + 1
                    : 0;

        return days;
    };

    // Helper function to get per diem rate based on recipient type
    const getPerDiemRate = (recipientType) => {
        if (!annualRates?.per_diem || !Array.isArray(annualRates.per_diem)) {
            return 0;
        }

        const perDiemRecord = annualRates.per_diem.find(
            record => record.recipient_type === recipientType
        );

        return perDiemRecord ? perDiemRecord.value : 0;
    };

    const [start_date, end_date, recipient_type] = watch([
        `${formKey}.start_date`,
        `${formKey}.end_date`,
        `${formKey}.recipient_type`,
    ]);

    // Update per diem rate when recipient type changes
    useEffect(() => {
        if (recipient_type) {
            const rate = getPerDiemRate(recipient_type);
            setCurrentPerDiemRate(rate);
        } else {
            setCurrentPerDiemRate(0);
        }
    }, [recipient_type, annualRates]);

    // Update amount when dates or recipient type changes
    useEffect(() => {
        if (start_date && end_date && currentPerDiemRate > 0) {
            try {
                const days = getTotalDays(start_date, end_date);
                setTotalDays(days);
                const newAmount = days * currentPerDiemRate;
                setValue(`${formKey}.amount`, newAmount);
            } catch (error) {
                console.error('Error calculating days:', error);
                setTotalDays(0);
            }
        } else {
            setTotalDays(0);
        }
    }, [start_date, end_date, currentPerDiemRate]);

    const [saving, setSaving] = useState(false);
    const hasErrors = Object.keys(errors).length > 0;
    const saveState = isDirty || existingUploads?.length;
    const saveText = saving || isSubmitting ? 'Saving...' : !saveState ? 'Done' : 'Save';

    const typeOptions = [
        {
            label: 'Client',
            value: 'client',
        },
        {
            label: 'Caregiver',
            value: 'caregiver',
        },
        {
            label: 'Both',
            value: 'both',
        },
    ];

    return (
        <div style={{ paddingTop: 20, backgroundColor: 'inherit' }}>
            <FormGroup>
                <SelectInput
                    name={`${formKey}.recipient_type`}
                    label='Individual'
                    options={typeOptions}
                    control={control}
                    required={true}
                    aria-required={true}
                />
            </FormGroup>
            <FormGroup>
                <TextInput
                    name={`${formKey}.recipient_name`}
                    label='Name'
                    control={control}
                    required={true}
                    aria-required={true}
                    maxLength={60}
                    newRules={{
                        maxLength: {
                            value: 60,
                            message: 'Name must be 60 characters or less'
                        }
                    }}
                />
            </FormGroup>
            <div className='d-sm-flex gap-3 mb-3'>
                <div className='flex-1'>
                    <TextInput
                        name={`${formKey}.start_date`}
                        label='Start Date'
                        type='date'
                        control={control}
                        required={true}
                        aria-required={true}
                    />
                </div>
                <div className='flex-1'>
                    <TextInput
                        name={`${formKey}.end_date`}
                        label='End Date'
                        type='date'
                        control={control}
                        required={true}
                        aria-required={true}
                    />
                </div>
                <div className='flex-1'>
                    <Label>Total Days</Label>
                    <p>{totalDays}</p>
                </div>
            </div>
            <FormGroup>
                <Label className='mb-0' for='perDayExpense'>
                    Rate Per Day
                </Label>
                <p className='pt-3'>${currentPerDiemRate}</p>
            </FormGroup>
            <Label>Total</Label>
            <p>{toUSD(vals?.amount)}</p>
            {hasValidId && <UploadDocument />}
            <DocumentInfoButton categoryValue="food_incidentals" />
            <ButtonGroup className='d-grid d-sm-flex align-items-center gap-2 mt-4'>
                <Button
                    color='outline-dark'
                    onClick={handleCancelItem}
                    disabled={isSubmitting}
                >
                    Cancel
                </Button>
                {hasValidId && (
                    <>
                        <Button
                            color='danger'
                            onClick={handleDeleteItem}
                            disabled={isSubmitting}
                        >
                            Delete
                        </Button>
                        <Button
                            color='primary'
                            onClick={async () => {
                                // If nothing to save, close the form (Done)
                                if (!saveState) {
                                    fundRequestDispatch({ type: 'reset_expense_item' });
                                    return;
                                }
                                try {
                                    setSaving(true);
                                    await handleSubmission({ permanentSave: false });
                                } finally {
                                    setSaving(false);
                                }
                            }}
                            disabled={hasErrors || isSubmitting || saving || !isValid}
                        >
                            {saveText}
                        </Button>
                    </>
                )}
                {!hasValidId && (
                    <Button
                        color='primary'
                        onClick={async () => {
                            // If nothing to save, close the form (Done)
                            if (!saveState) {
                                fundRequestDispatch({ type: 'reset_expense_item' });
                                return;
                            }
                            try {
                                setSaving(true);
                                const updatedItem = await handleSubmission({ permanentSave: false });
                                if (updatedItem && typeof updatedItem.id === 'number') {
                                    fundRequestDispatch({
                                        type: 'set_expense_item',
                                        expenseItem: updatedItem,
                                        expenseType: 'food',
                                    });
                                }
                            } finally {
                                setSaving(false);
                            }
                        }}
                        disabled={hasErrors || isSubmitting || saving || !isValid}
                    >
                        {saving || isSubmitting ? `Saving...` : `Next: Upload Documents`}
                    </Button>
                )}
            </ButtonGroup>
        </div>
    );
};

export default FoodExpenseForm;
